import { Entity } from '@api/api/entity-schema';
import {
  ChartAssetData,
  ChartColors,
  ChartEmpty,
  ChartError,
  CreateChartOptions,
  CreateOptionsConfig,
} from '@chart';
import { useWindowResize } from '@helpers/hooks/use-window-resize/useWindowResize';
import { config } from '@terminal/config';
import { useLanguageStore } from '@terminal/stores/use-language-store/useLanguageStore';
import clsx from 'clsx';
import { CSSProperties, ReactNode, RefObject, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import styles from './ChartAreaWrapper.module.scss';

export interface ChartAreaWrapperProps {
  className?: string;
  rootStyle?: CSSProperties;
  assetWithError?: Entity;
  hasError: boolean;
  hasEmpty: boolean;
  colors: ChartColors;
  chartData: ChartAssetData[];
  chartConfig?: Partial<CreateOptionsConfig>;
  onSearchAgain?: () => void;
  onRetry: () => void;
  renderChartArea: (
    options: CreateChartOptions,
    containerRef: RefObject<HTMLDivElement | null>
  ) => ReactNode;
}

export const ChartAreaWrapper = ({
  className,
  rootStyle,
  assetWithError,
  hasError,
  hasEmpty,
  colors,
  chartData,
  chartConfig,
  renderChartArea,
  onSearchAgain,
}: ChartAreaWrapperProps) => {
  const targetRef = useRef<HTMLDivElement>(null);
  const { width, height } = useWindowResize({ targetRef });
  const languageSetting = useLanguageStore(state => state.languageSetting);

  const { t } = useTranslation();

  const options: CreateChartOptions = {
    width,
    height,
    panePaddingTop: chartConfig?.panePaddingTop ?? 0,
    panePaddingRight: chartConfig?.panePaddingRight ?? 0,
    gutters: {
      x: 40,
      y: 40,
    },
    // TODO when this is packaged make sure this parent component adds robot mono and nort
    font: {
      default: '12px Nort',
      mono: '12px Roboto Mono',
    },
    config: chartConfig,
    colors,
    language: languageSetting,
  };

  const hasSize = !!(width && height);

  const emptyLocales = {
    imgAlt: t('chart:empty.imgAlt'),
    title: t('chart:empty.title', { asset: assetWithError?.name }),
    description: t('chart:empty.description'),
    btnLabel: t('chart:empty.btnLabel'),
  };

  const errorLocales = {
    imgAlt: t('chart:error.imgAlt'),
    title: t('chart:error.title'),
    description: t('chart:error.description'),
    linkAlt: t('chart:error.linkAlt'),
  };

  return (
    <div
      className={clsx(styles.container, className)}
      style={rootStyle}
      ref={targetRef}
      data-clipboard-snapshot
    >
      {hasError && (
        <ChartError supportEmail={config.supportEmail} locales={errorLocales} />
      )}
      {((!hasError && assetWithError) || hasEmpty) && (
        <ChartEmpty onSearchAgain={onSearchAgain} locales={emptyLocales} />
      )}
      {!!chartData.length &&
        !hasEmpty &&
        hasSize &&
        renderChartArea(options, targetRef)}
    </div>
  );
};
